import { get, isEmpty } from "lodash";
import { applyFunction, evaluate, format } from "./action";
import {
  IAction,
  IBase,
  IBarChart,
  IDisplay,
  IGetValue,
  IMetadata,
  IModel,
  IOutput,
  IResult,
  ITemplate,
  ITimeChart,
  IWaterfallChart,
  I3dSurface,
  IPieChart,
  IRawPlotlyChart,
  I2dStructure,
  I2dFigure,
  IWaterfallStructure,
  IWaterfallFigure,
  I3dStructure,
  I3dFigure,
  IStructure,
  ILabel,
  ITimeFigure,
  IValue,
} from "./types";

const MAX_SAMPLE_ITEMS = 6;
const MAX_ARRAY_ITEMS = 12;
const MAX_OBJECT_KEYS = 20;
const MAX_STRING_LENGTH = 4000;
const OUTPUT_CONTROL_TYPES = new Set(["checkbox", "radio", "select"]);

export interface IAssistantOutputControlSummary {
  path: string;
  label: string;
  type: string;
  value: unknown;
  checked?: boolean;
  disabled?: boolean;
}

export interface IAssistantChartContext {
  name: string;
  label: string;
  type: "bar_chart" | "waterfall_chart" | "pie_chart" | "time_chart" | "3d_surface" | "raw_plotly_chart";
  narrative: string;
  digest: Record<string, unknown>;
  view_state?: Record<string, unknown>;
}

const CHART_OUTPUT_TYPES = new Set([
  "bar_chart",
  "waterfall_chart",
  "pie_chart",
  "time_chart",
  "3d_surface",
  "raw_plotly_chart",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const notNull = <T,>(value: T | null | undefined): value is T => value !== null && value !== undefined;

const asRecordArray = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const formatDisplayLabel = (item: IBase): string => {
  const base = item.label?.trim() || item.name;
  const info = item.info?.trim();
  return info ? `${base} (${info})` : base;
};

const formatScalar = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return stringifyValue(value);
};

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  const seen = new WeakSet<object>();
  const replacer = (_key: string, val: unknown) => {
    if (typeof val === "string") {
      return val.length > 500 ? `${val.slice(0, 500)}... (${val.length - 500} more chars)` : val;
    }
    if (Array.isArray(val)) {
      if (val.length <= MAX_ARRAY_ITEMS) {
        return val;
      }
      const headCount = Math.ceil(MAX_ARRAY_ITEMS / 2);
      const tailCount = Math.floor(MAX_ARRAY_ITEMS / 2);
      const head = val.slice(0, headCount);
      const tail = val.slice(-tailCount);
      return [...head, `... (${val.length - head.length - tail.length} more items)`, ...tail];
    }
    if (val && typeof val === "object") {
      const obj = val as Record<string, unknown>;
      if (seen.has(obj)) {
        return "[Circular]";
      }
      seen.add(obj);
      const keys = Object.keys(obj);
      if (keys.length <= MAX_OBJECT_KEYS) {
        return obj;
      }
      const trimmed: Record<string, unknown> = {};
      keys.slice(0, MAX_OBJECT_KEYS).forEach((key) => {
        trimmed[key] = obj[key];
      });
      trimmed["..."] = `${keys.length - MAX_OBJECT_KEYS} more keys`;
      return trimmed;
    }
    return val;
  };

  try {
    const json = JSON.stringify(value, replacer, 2);
    if (!json) {
      return String(value);
    }
    if (json.length <= MAX_STRING_LENGTH) {
      return json;
    }
    return `${json.slice(0, MAX_STRING_LENGTH)}... (${json.length - MAX_STRING_LENGTH} more chars)`;
  } catch (error) {
    return String(value);
  }
};

const renderDisplayLabel = (display?: IDisplay): string | undefined => {
  if (!display) {
    return undefined;
  }

  const label = display.label?.trim() || "";
  const info = display.info?.trim();
  return info ? `${label} (${info})` : label || undefined;
};

const toFiniteNumbers = (values: unknown[]): number[] => {
  return values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
};

const summarizeNumericValues = (values: unknown[]) => {
  const numbers = toFiniteNumbers(values);
  if (!numbers.length) {
    return null;
  }

  return {
    count: numbers.length,
    min: Math.min(...numbers),
    max: Math.max(...numbers),
    first: numbers[0],
    last: numbers[numbers.length - 1],
    latest: numbers[numbers.length - 1],
  };
};

const sampleObjects = <T,>(values: T[], limit: number = 4): T[] => {
  const indices = sampleIndices(values.length).slice(0, limit);
  return indices.map(index => values[index]);
};

const summarizeAxisValues = (values: unknown[]) => {
  if (!values.length) {
    return null;
  }

  const numericValues = toFiniteNumbers(values);
  if (numericValues.length === values.length) {
    return {
      min: Math.min(...numericValues),
      max: Math.max(...numericValues),
    };
  }

  return {
    sample: sampleObjects(values.map(value => formatScalar(value))),
  };
};

const takeTopByAbsoluteValue = (x: unknown[], y: unknown[], limit: number = 3) => {
  const entries = x
    .map((xValue, index) => ({
      x: formatScalar(xValue),
      y: y[index],
    }))
    .filter(entry => typeof entry.y === "number" && Number.isFinite(entry.y))
    .sort((left, right) => Math.abs(right.y as number) - Math.abs(left.y as number))
    .slice(0, limit);

  return entries.map(entry => ({
    x: entry.x,
    y: entry.y as number,
  }));
};

const buildSeriesDigest = (name: string, x: unknown[], y: unknown[]) => {
  const length = Math.min(x.length, y.length);
  const trimmedX = x.slice(0, length);
  const trimmedY = y.slice(0, length);

  return {
    name,
    point_count: length,
    stats: summarizeNumericValues(trimmedY),
    top_points: takeTopByAbsoluteValue(trimmedX, trimmedY),
    sample: sampleObjects(
      trimmedX.map((xValue, index) => ({
        x: formatScalar(xValue),
        y: trimmedY[index],
      })),
    ),
  };
};

const describeSeriesDigest = (
  label: string,
  digest: ReturnType<typeof buildSeriesDigest>,
): string => {
  const stats = digest.stats;
  if (!stats) {
    return `Series "${label}" has ${digest.point_count} points.`;
  }

  return `Series "${label}" has ${digest.point_count} points with values from ${stats.min} to ${stats.max}; latest ${stats.latest}.`;
};

const buildSliceDigest = (labels: unknown[], values: unknown[]) => {
  const length = Math.min(labels.length, values.length);
  const entries = Array.from({ length }, (_value, index) => ({
    label: formatScalar(labels[index]),
    value: values[index],
  })).filter(entry => typeof entry.value === "number" && Number.isFinite(entry.value));

  const total = entries.reduce((sum, entry) => sum + (entry.value as number), 0);
  const ranked = [...entries].sort((left, right) => (right.value as number) - (left.value as number));

  return {
    slice_count: entries.length,
    total,
    top_slices: ranked.slice(0, 3).map(entry => ({
      label: entry.label,
      value: entry.value as number,
      percentage: total > 0 ? Number((((entry.value as number) / total) * 100).toFixed(2)) : null,
    })),
    sample: sampleObjects(entries),
  };
};

const summarizeAxisTriples = (x: unknown[], y: unknown[], z: unknown[]) => {
  const length = Math.min(x.length, y.length, z.length);
  const trimmedX = x.slice(0, length);
  const trimmedY = y.slice(0, length);
  const trimmedZ = z.slice(0, length);
  const zNumbers = toFiniteNumbers(trimmedZ);
  const sample = sampleObjects(trimmedX.map((xValue, index) => ({
    x: formatScalar(xValue),
    y: formatScalar(trimmedY[index]),
    z: trimmedZ[index],
  })));

  let peak: { x: string; y: string; z: unknown } | undefined;
  let valley: { x: string; y: string; z: unknown } | undefined;
  if (zNumbers.length === trimmedZ.length && trimmedZ.length > 0) {
    let peakIndex = 0;
    let valleyIndex = 0;
    trimmedZ.forEach((zValue, index) => {
      if ((zValue as number) > (trimmedZ[peakIndex] as number)) {
        peakIndex = index;
      }
      if ((zValue as number) < (trimmedZ[valleyIndex] as number)) {
        valleyIndex = index;
      }
    });

    peak = {
      x: formatScalar(trimmedX[peakIndex]),
      y: formatScalar(trimmedY[peakIndex]),
      z: trimmedZ[peakIndex],
    };
    valley = {
      x: formatScalar(trimmedX[valleyIndex]),
      y: formatScalar(trimmedY[valleyIndex]),
      z: trimmedZ[valleyIndex],
    };
  }

  return {
    point_count: length,
    x_range: summarizeAxisValues(trimmedX),
    y_range: summarizeAxisValues(trimmedY),
    z_range: summarizeAxisValues(trimmedZ),
    peak,
    valley,
    sample,
  };
};

const sampleIndices = (length: number): number[] => {
  if (length <= MAX_SAMPLE_ITEMS) {
    return Array.from({ length }, (_v, i) => i);
  }
  const headCount = Math.ceil(MAX_SAMPLE_ITEMS / 2);
  const tailCount = Math.floor(MAX_SAMPLE_ITEMS / 2);
  const head = Array.from({ length: headCount }, (_v, i) => i);
  const tailStart = Math.max(length - tailCount, headCount);
  const tail = Array.from({ length: length - tailStart }, (_v, i) => tailStart + i);
  return [...head, ...tail];
};

const getEffectiveValue = (model: IModel, result: IResult, path: string): unknown => {
  const modelValue = get(model, path);
  if (modelValue !== undefined) {
    return modelValue;
  }

  return get(result, path);
};

const buildAssistantGetValue = (model: IModel, result: IResult): IGetValue => {
  return ((path: string) => getEffectiveValue(model, result, path) as IValue) as IGetValue;
};

const isOutputItemHidden = (
  item: IBase,
  model: IModel,
  result: IResult,
  metadata?: IMetadata,
): boolean => {
  if (metadata?.hidden?.includes(item.name)) {
    return true;
  }

  return Boolean(item.hidden && evaluate(model, item.hidden, buildAssistantGetValue(model, result), result));
};

const isOutputControlDisabled = (item: IBase, model: IModel, result: IResult): boolean => {
  const control = item as IBase & { disable?: boolean | { sources: string[]; expression: string } };
  if (typeof control.disable === "boolean") {
    return control.disable;
  }

  return Boolean(
    control.disable &&
    evaluate(model, control.disable, buildAssistantGetValue(model, result), result),
  );
};

const getChartParentProperty = (chartName: string, metadata?: IMetadata): Record<string, unknown> | undefined => {
  const parentName = metadata?.outputs?.findNode(chartName)?.parent?.data?.name as string | undefined;
  if (!parentName) {
    return undefined;
  }

  const property = metadata?.properties?.[parentName];
  if (!property || typeof property !== "object") {
    return undefined;
  }

  return property as Record<string, unknown>;
};

const summarizeNumbers = (values: unknown[]): string => {
  const numbers = values.filter((v) => typeof v === "number" && Number.isFinite(v)) as number[];
  if (!numbers.length) {
    return "";
  }
  let min = numbers[0];
  let max = numbers[0];
  for (const value of numbers) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return `count=${numbers.length}, min=${min}, max=${max}, first=${numbers[0]}, last=${numbers[numbers.length - 1]}`;
};

const formatSeriesSample = (x: unknown[], y: unknown[]): string => {
  const length = Math.min(x.length, y.length);
  if (!length) {
    return "[]";
  }
  const indices = sampleIndices(length);
  const pairs = indices.map((i) => `(${formatScalar(x[i])}, ${formatScalar(y[i])})`);
  return `[${pairs.join(", ")}]`;
};

const describeSeries = (name: string, x: unknown[], y: unknown[]): string => {
  const length = Math.min(x.length, y.length);
  if (!length) {
    return `Series "${name}": no data.`;
  }
  const stats = summarizeNumbers(y);
  const sample = formatSeriesSample(x, y);
  const statsText = stats ? ` ${stats}.` : "";
  return `Series "${name}": points=${length}.${statsText} sample=${sample}`;
};

const describeSlices = (name: string, labels: unknown[], values: unknown[]): string => {
  const length = Math.min(labels.length, values.length);
  if (!length) {
    return `Slices "${name}": no data.`;
  }
  const indices = sampleIndices(length);
  const pairs = indices.map((i) => `${formatScalar(labels[i])}=${formatScalar(values[i])}`);
  const suffix = length > indices.length ? `, ... (${length - indices.length} more slices)` : "";
  return `Slices "${name}": ${pairs.join(", ")}${suffix}`;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const buildSankeyDigest = (name: string, trace: Record<string, unknown>) => {
  const rawNodeLabels = Array.isArray(get(trace, "node.label")) ? (get(trace, "node.label") as Array<unknown>) : [];
  const nodeLabels = rawNodeLabels.map((label, index) => {
    const formatted = formatScalar(label);
    return formatted && formatted !== "N/A" ? formatted : `Node ${index}`;
  });
  const sources = Array.isArray(get(trace, "link.source")) ? (get(trace, "link.source") as Array<unknown>) : [];
  const targets = Array.isArray(get(trace, "link.target")) ? (get(trace, "link.target") as Array<unknown>) : [];
  const values = Array.isArray(get(trace, "link.value")) ? (get(trace, "link.value") as Array<unknown>) : [];
  const length = Math.min(sources.length, targets.length, values.length);

  const links = Array.from({ length }, (_value, index) => {
    const sourceIndex = toFiniteNumber(sources[index]);
    const targetIndex = toFiniteNumber(targets[index]);
    const linkValue = toFiniteNumber(values[index]);

    if (sourceIndex === null || targetIndex === null || linkValue === null) {
      return null;
    }

    const sourceLabel = nodeLabels[sourceIndex] ?? `Node ${sourceIndex}`;
    const targetLabel = nodeLabels[targetIndex] ?? `Node ${targetIndex}`;

    return {
      source: sourceLabel,
      target: targetLabel,
      value: linkValue,
    };
  }).filter(notNull);

  const totalFlow = links.reduce((sum, link) => sum + link.value, 0);

  return {
    name,
    node_count: nodeLabels.length,
    link_count: links.length,
    total_flow: Number(totalFlow.toFixed(4)),
    top_links: [...links]
      .sort((left, right) => Math.abs(right.value) - Math.abs(left.value))
      .slice(0, 3),
    sample_nodes: sampleObjects(nodeLabels),
  };
};

const describeSankey = (name: string, trace: Record<string, unknown>): string => {
  const digest = buildSankeyDigest(name, trace);
  if (!digest.link_count) {
    return `Sankey "${name}": nodes=${digest.node_count}, no links.`;
  }

  const lead = digest.top_links[0];
  const leadText = lead
    ? ` top flow=${lead.source} -> ${lead.target} (${formatScalar(lead.value)})`
    : "";

  return `Sankey "${name}": nodes=${digest.node_count}, links=${digest.link_count}, total_flow=${formatScalar(digest.total_flow)}.${leadText}`;
};

const summarizeBarChart = (chart: IBarChart, result: IResult): string[] => {
  return chart.figures
    .map((figure, index) => {
      const isStructure = (figure as I2dStructure).type === "structure";
      const label =
        figure.label?.trim() ||
        (!isStructure && (figure as I2dFigure).y_data?.label) ||
        (isStructure && (figure as I2dStructure).label) ||
        `Series ${index + 1}`;
      const raw = get(result, (figure as I2dStructure).name, []) as unknown;
      const entries = asRecordArray(raw);
      const x = isStructure
        ? entries.map((v) => v[(figure as I2dStructure).x_value])
        : (Array.isArray(get(result, (figure as I2dFigure).x_data.name, []))
          ? (get(result, (figure as I2dFigure).x_data.name, []) as Array<any>)
          : []);
      const y = isStructure
        ? entries.map((v) => v[(figure as I2dStructure).y_value])
        : (Array.isArray(get(result, (figure as I2dFigure).y_data.name, []))
          ? (get(result, (figure as I2dFigure).y_data.name, []) as Array<any>)
          : []);
      return describeSeries(label, x, y);
    })
    .filter((line) => Boolean(line));
};

const summarizeWaterfall = (chart: IWaterfallChart, result: IResult): string[] => {
  return chart.figures
    .map((figure, index) => {
      const isStructure = (figure as IWaterfallStructure).type === "structure";
      const label =
        figure.label?.trim() ||
        (!isStructure && (figure as IWaterfallFigure).y_data?.label) ||
        (isStructure && (figure as IWaterfallStructure).label) ||
        `Series ${index + 1}`;
      const raw = get(result, (figure as IWaterfallStructure).name, []) as unknown;
      const entries = asRecordArray(raw);
      const x = isStructure
        ? entries.map((v) => v[(figure as IWaterfallStructure).x_value])
        : (Array.isArray(get(result, (figure as IWaterfallFigure).x_data.name, []))
          ? (get(result, (figure as IWaterfallFigure).x_data.name, []) as Array<any>)
          : []);
      const y = isStructure
        ? entries.map((v) => v[(figure as IWaterfallStructure).y_value])
        : (Array.isArray(get(result, (figure as IWaterfallFigure).y_data.name, []))
          ? (get(result, (figure as IWaterfallFigure).y_data.name, []) as Array<any>)
          : []);
      return describeSeries(label, x, y);
    })
    .filter((line) => Boolean(line));
};

const summarizePieChart = (chart: IPieChart, result: IResult): string[] => {
  return chart.figures
    .map((figure, index) => {
      const name = figure.label?.trim() || `Figure ${index + 1}`;
      if (Array.isArray(figure.data)) {
        const labels = figure.data.map((data) => data.label || data.name);
        const values = figure.data.map((data) =>
          data.value === undefined ? get(result, data.name, []) : data.value
        );
        return describeSlices(name, labels, values);
      }
      const structure = figure.data as IStructure;
      const raw = get(result, structure.name, []) as unknown;
      const entries = asRecordArray(raw);
      const values = entries.map((item) => item[structure.value]);
      const labels = entries.map((item) => item[structure.label]);
      return describeSlices(name, labels, values);
    })
    .filter((line) => Boolean(line));
};

const summarize3dSurface = (chart: I3dSurface, result: IResult): string[] => {
  return chart.figures
    .map((figure, index) => {
      const isStructure = (figure as I3dStructure).type === "structure";
      const label = figure.label?.trim() || `Surface ${index + 1}`;
      const raw = get(result, (figure as I3dStructure).name, []) as unknown;
      const entries = asRecordArray(raw);
      const x = isStructure
        ? entries.map((v) => v[(figure as I3dStructure).x_value])
        : (Array.isArray(get(result, (figure as I3dFigure).x_data.name, []))
          ? (get(result, (figure as I3dFigure).x_data.name, []) as Array<any>)
          : []);
      const y = isStructure
        ? entries.map((v) => v[(figure as I3dStructure).y_value])
        : (Array.isArray(get(result, (figure as I3dFigure).y_data.name, []))
          ? (get(result, (figure as I3dFigure).y_data.name, []) as Array<any>)
          : []);
      const z = isStructure
        ? entries.map((v) => v[(figure as I3dStructure).z_value])
        : (Array.isArray(get(result, (figure as I3dFigure).z_data.name, []))
          ? (get(result, (figure as I3dFigure).z_data.name, []) as Array<any>)
          : []);
      const length = Math.min(x.length, y.length, z.length);
      if (!length) {
        return `Surface "${label}": no data.`;
      }
      const indices = sampleIndices(length);
      const triples = indices.map((i) => `(${formatScalar(x[i])}, ${formatScalar(y[i])}, ${formatScalar(z[i])})`);
      const suffix = length > indices.length ? `, ... (${length - indices.length} more points)` : "";
      return `Surface "${label}": points=${length}, sample=[${triples.join(", ")}]${suffix}`;
    })
    .filter((line) => Boolean(line));
};

const summarizeTimeChart = (chart: ITimeChart, model: IModel, result: IResult): string[] => {
  const lines: string[] = [];
  chart.figures.forEach((figure: ITimeFigure, figIndex) => {
    figure.data.forEach((series, seriesIndex) => {
      const raw = get(result, series.name, []);
      const y = Array.isArray(raw) ? raw : [];
      if (!y.length) {
        return;
      }
      const label =
        [figure.label?.trim() || `Figure ${figIndex + 1}`, series.label?.trim() || `Series ${seriesIndex + 1}`]
          .filter(Boolean)
          .join(" - ");
      const start =
        (figure.startname && (get(result, figure.startname) || get(model, figure.startname))) || figure.start || "unknown";
      const stats = summarizeNumbers(y);
      const sample = sampleIndices(y.length).map((i) => formatScalar(y[i]));
      const suffix = y.length > sample.length ? `, ... (${y.length - sample.length} more points)` : "";
      const statsText = stats ? ` ${stats}.` : "";
      lines.push(
        `Series "${label}": points=${y.length}.${statsText} interval=${series.interval} minutes, start=${start}, sample=[${sample.join(", ")}]${suffix}`
      );
    });
  });
  return lines;
};

const summarizeRawPlotly = (chart: IRawPlotlyChart, model: IModel, result: IResult): string[] => {
  let plotDataArray: Array<any> = [];
  try {
    plotDataArray = (
      chart.dataName
        ? get(result, chart.dataName, [])
        : chart.data
          ? applyFunction(model, chart.data, undefined, result) ?? []
          : []
    ) as Array<any>;
  } catch (error) {
    return ["Unable to parse plotly data."];
  }

  if (!Array.isArray(plotDataArray) || !plotDataArray.length) {
    return ["No plotly data available."];
  }

  return plotDataArray.map((series, index) => {
    const label = series?.name || `Series ${index + 1}`;
    const traceType = inferPlotlyTraceType(series as Record<string, unknown>);
    if (traceType === "sankey") {
      return describeSankey(label, series as Record<string, unknown>);
    }
    if (traceType === "pie") {
      return describeSlices(
        label,
        Array.isArray(series?.labels) ? series.labels : [],
        Array.isArray(series?.values) ? series.values : [],
      );
    }
    const x = Array.isArray(series?.x) ? series.x : [];
    const y = Array.isArray(series?.y) ? series.y : [];
    return describeSeries(label, x, y);
  });
};

const buildBarChartContext = (chart: IBarChart, result: IResult): IAssistantChartContext | null => {
  const series = chart.figures
    .map((figure, index) => {
      const isStructure = (figure as I2dStructure).type === "structure";
      const label =
        figure.label?.trim() ||
        (!isStructure && (figure as I2dFigure).y_data?.label) ||
        (isStructure && (figure as I2dStructure).label) ||
        `Series ${index + 1}`;
      const raw = get(result, (figure as I2dStructure).name, []) as unknown;
      const entries = asRecordArray(raw);
      const x = isStructure
        ? entries.map((value) => value[(figure as I2dStructure).x_value])
        : (Array.isArray(get(result, (figure as I2dFigure).x_data.name, []))
          ? (get(result, (figure as I2dFigure).x_data.name, []) as Array<unknown>)
          : []);
      const y = isStructure
        ? entries.map((value) => value[(figure as I2dStructure).y_value])
        : (Array.isArray(get(result, (figure as I2dFigure).y_data.name, []))
          ? (get(result, (figure as I2dFigure).y_data.name, []) as Array<unknown>)
          : []);

      return buildSeriesDigest(label, x, y);
    })
    .filter(seriesDigest => seriesDigest.point_count > 0);

  if (!series.length) {
    return null;
  }

  const label = formatDisplayLabel(chart);
  const axes = {
    x: renderDisplayLabel(chart.axis?.[0] || (chart.figures?.[0] as I2dFigure | undefined)?.x_data),
    y: renderDisplayLabel(chart.axis?.[1] || (chart.figures?.[0] as I2dFigure | undefined)?.y_data),
  };

  return {
    name: chart.name,
    label,
    type: "bar_chart",
    narrative: `${label} is a ${chart.stack ? "stacked" : "grouped"} ${chart.horizontal ? "horizontal" : "vertical"} bar chart with ${series.length} series. ${describeSeriesDigest(series[0].name, series[0])}`,
    digest: {
      orientation: chart.horizontal ? "horizontal" : "vertical",
      stacking: chart.stack ? "stacked" : "grouped",
      axes,
      series,
    },
  };
};

const assignWaterfallMeasure = (
  chart: IWaterfallChart,
  y: Array<unknown>,
  measure: Array<"absolute" | "relative" | "total"> | undefined,
) => {
  if (chart.measure?.length === y.length) {
    return chart.measure;
  }

  if (measure?.length === y.length) {
    return measure;
  }

  return y.map((value, index) => (index === 0 ? "absolute" : value === null ? "total" : "relative"));
};

const buildWaterfallFigureData = (figure: IWaterfallStructure | IWaterfallFigure, result: IResult) => {
  if ((figure as IWaterfallStructure).type === "structure") {
    const currentFigure = figure as IWaterfallStructure;
    const values = get(result, currentFigure.name, []) as Array<Record<string, unknown>>;
    return {
      name: formatDisplayLabel(currentFigure),
      x: values.map(value => value[currentFigure.x_value]),
      y: values.map(value => value[currentFigure.y_value]),
      measure: currentFigure.m_value
        ? values.map(value => value[currentFigure.m_value as string]) as Array<"absolute" | "relative" | "total">
        : undefined,
    };
  }

  const currentFigure = figure as IWaterfallFigure;
  return {
    name: renderDisplayLabel(currentFigure.y_data) ?? currentFigure.label?.trim() ?? "Series",
    x: get(result, currentFigure.x_data.name, []) as Array<unknown>,
    y: get(result, currentFigure.y_data.name, []) as Array<unknown>,
    measure: currentFigure.m_data
      ? get(result, currentFigure.m_data.name, []) as Array<"absolute" | "relative" | "total">
      : undefined,
  };
};

const buildWaterfallSteps = (
  x: unknown[],
  y: unknown[],
  measure: Array<"absolute" | "relative" | "total">,
) => {
  let runningTotal: number = 0;
  return Array.from({ length: Math.min(x.length, y.length, measure.length) }, (_value, index) => {
    const stepMeasure = measure[index];
    const currentValue = y[index];
    const rawValue: number | null =
      typeof currentValue === "number" && Number.isFinite(currentValue)
        ? currentValue
        : null;

    switch (stepMeasure) {
      case "absolute":
        runningTotal = rawValue ?? runningTotal;
        break;
      case "relative":
        runningTotal += rawValue ?? 0;
        break;
      case "total":
        break;
      default:
        break;
    }

    return {
      label: formatScalar(x[index]),
      measure: stepMeasure,
      raw_value: rawValue,
      running_total: runningTotal,
    };
  });
};

const buildWaterfallChartContext = (chart: IWaterfallChart, result: IResult): IAssistantChartContext | null => {
  const series = chart.figures
    .map(figure => {
      const figureData = buildWaterfallFigureData(figure as IWaterfallStructure | IWaterfallFigure, result);
      const measure = assignWaterfallMeasure(chart, figureData.y, figureData.measure);
      const steps = buildWaterfallSteps(figureData.x, figureData.y, measure);
      if (!steps.length) {
        return null;
      }

      const relativeSteps = steps.filter(
        (step): step is typeof step & { raw_value: number } =>
          step.measure === "relative" && typeof step.raw_value === "number",
      );
      const sortedRelative = [...relativeSteps].sort((left, right) => Math.abs(right.raw_value ?? 0) - Math.abs(left.raw_value ?? 0));

      return {
        name: figureData.name,
        step_count: steps.length,
        final_total: steps[steps.length - 1]?.running_total ?? null,
        largest_change: sortedRelative[0] ?? null,
        sample_steps: sampleObjects(steps),
      };
    })
    .filter(notNull);

  if (!series.length) {
    return null;
  }

  const label = formatDisplayLabel(chart);
  const firstSeries = series[0];

  return {
    name: chart.name,
    label,
    type: "waterfall_chart",
    narrative: `${label} is a waterfall chart with ${series.length} series. "${firstSeries.name}" ends at ${firstSeries.final_total}.`,
    digest: {
      concatenate: Boolean(chart.concatenate),
      axes: {
        x: renderDisplayLabel(chart.axis?.[0] || (chart.figures?.[0] as IWaterfallFigure | undefined)?.x_data),
        y: renderDisplayLabel(chart.axis?.[1] || (chart.figures?.[0] as IWaterfallFigure | undefined)?.y_data),
      },
      series,
    },
  };
};

const buildPieChartContext = (chart: IPieChart, result: IResult): IAssistantChartContext | null => {
  const figures = chart.figures
    .map((figure, index) => {
      const figureLabel = figure.label?.trim() || `Figure ${index + 1}`;
      let labels: unknown[] = [];
      let values: unknown[] = [];

      if (Array.isArray(figure.data)) {
        labels = figure.data.map(data => data.label || data.name);
        values = figure.data.map((data) => data.value === undefined ? get(result, data.name, []) : data.value);
      } else {
        const structure = figure.data as IStructure;
        const entries = asRecordArray(get(result, structure.name, []) as unknown);
        labels = entries.map(entry => entry[structure.label]);
        values = entries.map(entry => entry[structure.value]);
      }

      const digest = buildSliceDigest(labels, values);
      if (!digest.slice_count) {
        return null;
      }

      return {
        name: figureLabel,
        ...digest,
      };
    })
    .filter(notNull);

  if (!figures.length) {
    return null;
  }

  const label = formatDisplayLabel(chart);
  const leadFigure = figures[0];
  const leadSlice = leadFigure.top_slices[0];

  return {
    name: chart.name,
    label,
    type: "pie_chart",
    narrative: `${label} has ${figures.length} pie ${figures.length === 1 ? "figure" : "figures"}. Largest slice is ${leadSlice?.label ?? "N/A"}${leadSlice ? ` at ${leadSlice.percentage}%` : ""}.`,
    digest: {
      figures,
    },
  };
};

const build3dSurfaceContext = (chart: I3dSurface, result: IResult): IAssistantChartContext | null => {
  const surfaces = chart.figures
    .map((figure, index) => {
      const isStructure = (figure as I3dStructure).type === "structure";
      const label =
        figure.label?.trim() ||
        (isStructure ? (figure as I3dStructure).label : undefined) ||
        `Surface ${index + 1}`;
      const raw = get(result, (figure as I3dStructure).name, []) as unknown;
      const entries = asRecordArray(raw);
      const x = isStructure
        ? entries.map(value => value[(figure as I3dStructure).x_value])
        : (get(result, (figure as I3dFigure).x_data.name, []) as Array<unknown>);
      const y = isStructure
        ? entries.map(value => value[(figure as I3dStructure).y_value])
        : (get(result, (figure as I3dFigure).y_data.name, []) as Array<unknown>);
      const z = isStructure
        ? entries.map(value => value[(figure as I3dStructure).z_value])
        : (get(result, (figure as I3dFigure).z_data.name, []) as Array<unknown>);

      const digest = summarizeAxisTriples(x, y, z);
      if (!digest.point_count) {
        return null;
      }

      return {
        name: label,
        ...digest,
      };
    })
    .filter(notNull);

  if (!surfaces.length) {
    return null;
  }

  const label = formatDisplayLabel(chart);
  return {
    name: chart.name,
    label,
    type: "3d_surface",
    narrative: `${label} has ${surfaces.length} surface ${surfaces.length === 1 ? "layer" : "layers"}. ${surfaces[0].name} contains ${surfaces[0].point_count} plotted points.`,
    digest: {
      axes: {
        x: renderDisplayLabel(chart.axis?.[0] || (chart.figures?.[0] as I3dFigure | undefined)?.x_data),
        y: renderDisplayLabel(chart.axis?.[1] || (chart.figures?.[0] as I3dFigure | undefined)?.y_data),
        z: renderDisplayLabel(chart.axis?.[2] || (chart.figures?.[0] as I3dFigure | undefined)?.z_data),
      },
      surfaces,
    },
  };
};

const buildTimeChartViewState = (chart: ITimeChart, metadata?: IMetadata) => {
  const property = getChartParentProperty(chart.name, metadata);
  if (!property || property.name !== chart.name) {
    return undefined;
  }

  return {
    sync: property.sync,
    update_type: property.type,
    x_range: get(property, "relayout.xaxis.range"),
    y_range: get(property, "relayout.yaxis.range"),
  };
};

const buildTimeChartContext = (
  chart: ITimeChart,
  model: IModel,
  result: IResult,
  metadata?: IMetadata,
): IAssistantChartContext | null => {
  const figures = chart.figures
    .map((figure, figureIndex) => {
      const figureLabel = figure.label?.trim() || `Figure ${figureIndex + 1}`;
      const series = figure.data
        .map((seriesEntry, seriesIndex) => {
          const values = get(result, seriesEntry.name, []) as Array<unknown>;
          if (!Array.isArray(values) || !values.length) {
            return null;
          }

          const rawStart =
            (figure.startname && (get(result, figure.startname) || get(model, figure.startname))) ||
            figure.start ||
            null;
          const start = typeof rawStart === "string" || typeof rawStart === "number"
            ? String(rawStart)
            : null;
          return {
            name: seriesEntry.label?.trim() || `Series ${seriesIndex + 1}`,
            interval_minutes: seriesEntry.interval,
            start,
            end: start && values.length > 1
              ? new Date(new Date(start).getTime() + (values.length - 1) * seriesEntry.interval * 60000).toISOString()
              : start,
            point_count: values.length,
            stats: summarizeNumericValues(values),
            sample: sampleObjects(values.map(value => formatScalar(value))),
          };
        })
        .filter(notNull);

      if (!series.length) {
        return null;
      }

      return {
        name: figureLabel,
        series,
      };
    })
    .filter(notNull);

  if (!figures.length) {
    return null;
  }

  const label = formatDisplayLabel(chart);
  const firstSeries = figures[0].series[0];
  return {
    name: chart.name,
    label,
    type: "time_chart",
    narrative: `${label} is a time chart with ${figures.length} figure ${figures.length === 1 ? "group" : "groups"}. ${firstSeries.name} spans ${firstSeries.point_count} points at ${firstSeries.interval_minutes}-minute intervals.`,
    digest: {
      axes: {
        x: renderDisplayLabel(chart.axis?.[0]),
        y: renderDisplayLabel(chart.axis?.[1]),
      },
      figures,
    },
    view_state: buildTimeChartViewState(chart, metadata),
  };
};

const buildRawPlotlyLayoutDigest = (chart: IRawPlotlyChart, model: IModel, result: IResult) => {
  const layoutCandidate = chart.layout;
  let evaluatedLayout: unknown = layoutCandidate;
  if (!chart.layoutName && layoutCandidate && isRecord(layoutCandidate) && Array.isArray((layoutCandidate as IAction).sources)) {
    try {
      evaluatedLayout = applyFunction(model, layoutCandidate as IAction, undefined, result);
    } catch (error) {
      return {};
    }
  }
  const layout = chart.layoutName
    ? get(result, chart.layoutName, {})
    : evaluatedLayout ?? {};

  if (!layout || typeof layout !== "object") {
    return {};
  }

  return {
    title: get(layout, "title.text") ?? get(layout, "title") ?? null,
    xaxis_title: get(layout, "xaxis.title.text") ?? get(layout, "xaxis.title") ?? null,
    yaxis_title: get(layout, "yaxis.title.text") ?? get(layout, "yaxis.title") ?? null,
    x_range: get(layout, "xaxis.range") ?? null,
    y_range: get(layout, "yaxis.range") ?? null,
  };
};

const inferPlotlyTraceType = (trace: Record<string, unknown>) => {
  const explicitType = typeof trace.type === "string" ? trace.type : undefined;
  if (explicitType) {
    return explicitType;
  }
  if (Array.isArray(trace.labels) && Array.isArray(trace.values)) {
    return "pie";
  }
  if (Array.isArray(trace.z)) {
    return "surface";
  }
  if (Array.isArray(trace.x) && Array.isArray(trace.y)) {
    return "scatter";
  }
  return "unknown";
};

const buildRawPlotlyChartContext = (
  chart: IRawPlotlyChart,
  model: IModel,
  result: IResult,
): IAssistantChartContext | null => {
  let rawTraces: unknown = [];
  try {
    rawTraces = (
      chart.dataName
        ? get(result, chart.dataName, [])
        : chart.data
          ? applyFunction(model, chart.data, undefined, result) ?? []
          : []
    );
  } catch (error) {
    return null;
  }
  const traces = asRecordArray(rawTraces);

  if (!traces.length) {
    return null;
  }

  const traceDigests = traces.map((trace, index) => {
    const traceType = inferPlotlyTraceType(trace);
    const name = typeof trace.name === "string" ? trace.name : `Trace ${index + 1}`;

    if (traceType === "pie") {
      const digest = buildSliceDigest(
        Array.isArray(trace.labels) ? trace.labels : [],
        Array.isArray(trace.values) ? trace.values : [],
      );

      return {
        name,
        type: traceType,
        ...digest,
      };
    }

    if (traceType === "surface") {
      const digest = summarizeAxisTriples(
        Array.isArray(trace.x) ? trace.x : [],
        Array.isArray(trace.y) ? trace.y : [],
        Array.isArray(trace.z) ? trace.z : [],
      );

      return {
        name,
        type: traceType,
        ...digest,
      };
    }

    if (traceType === "sankey") {
      return {
        type: traceType,
        ...buildSankeyDigest(name, trace),
      };
    }

    const x = Array.isArray(trace.x) ? trace.x : [];
    const y = Array.isArray(trace.y) ? trace.y : [];
    const digest = buildSeriesDigest(name, x, y);
    return {
      type: traceType,
      ...digest,
    };
  });

  const label = formatDisplayLabel(chart);
  const firstTraceDigest = traceDigests[0] as Record<string, unknown> | undefined;
  const sankeyNarrative = firstTraceDigest?.type === "sankey"
    ? ` Sankey trace has ${formatScalar(firstTraceDigest.node_count)} nodes and ${formatScalar(firstTraceDigest.link_count)} links.`
    : "";
  return {
    name: chart.name,
    label,
    type: "raw_plotly_chart",
    narrative: `${label} is a Plotly chart with ${traceDigests.length} trace${traceDigests.length === 1 ? "" : "s"}.${sankeyNarrative}`,
    digest: {
      layout: buildRawPlotlyLayoutDigest(chart, model, result),
      traces: traceDigests,
    },
  };
};

const buildChartContext = (
  item: IBase,
  model: IModel,
  result: IResult,
  metadata?: IMetadata,
): IAssistantChartContext | null => {
  switch (item.type) {
    case "bar_chart":
      return buildBarChartContext(item as IBarChart, result);
    case "waterfall_chart":
      return buildWaterfallChartContext(item as IWaterfallChart, result);
    case "pie_chart":
      return buildPieChartContext(item as IPieChart, result);
    case "time_chart":
      return buildTimeChartContext(item as ITimeChart, model, result, metadata);
    case "3d_surface":
      return build3dSurfaceContext(item as I3dSurface, result);
    case "raw_plotly_chart":
      return buildRawPlotlyChartContext(item as IRawPlotlyChart, model, result);
    default:
      return null;
  }
};

const renderOutputItem = (
  item: IBase,
  model: IModel,
  result: IResult,
  lines: string[],
  depth: number,
  metadata?: IMetadata,
) => {
  if (isOutputItemHidden(item, model, result, metadata)) {
    return;
  }

  const indent = "  ".repeat(depth);
  const pushLine = (line: string) => {
    if (!line) return;
    lines.push(`${indent}${line}`);
  };

  switch (item.type) {
    case "group":
    case "anchors":
    case "tabs":
    case "tab":
    case "explorer": {
      const label = formatDisplayLabel(item);
      if (label) {
        pushLine(`${label}:`);
      }
      (item as any).items?.forEach((child: IBase) => renderOutputItem(child, model, result, lines, depth + 1, metadata));
      break;
    }
    case "label": {
      const template = item as ILabel;
      const getValue = (path: string) => get(result, path) ?? get(model, path);
      const rawValue = template.format
        ? format(model, template.format, getValue, result)
        : getValue(template.name);
      if (rawValue === null || rawValue === undefined || rawValue === "") {
        return;
      }
      pushLine(`${formatDisplayLabel(template)}: ${formatScalar(rawValue)}`);
      break;
    }
    case "bar_chart": {
      pushLine(`${formatDisplayLabel(item)} (bar chart):`);
      summarizeBarChart(item as IBarChart, result).forEach((line) => lines.push(`${indent}  ${line}`));
      break;
    }
    case "waterfall_chart": {
      pushLine(`${formatDisplayLabel(item)} (waterfall chart):`);
      summarizeWaterfall(item as IWaterfallChart, result).forEach((line) => lines.push(`${indent}  ${line}`));
      break;
    }
    case "pie_chart": {
      pushLine(`${formatDisplayLabel(item)} (pie chart):`);
      summarizePieChart(item as IPieChart, result).forEach((line) => lines.push(`${indent}  ${line}`));
      break;
    }
    case "time_chart": {
      pushLine(`${formatDisplayLabel(item)} (time chart):`);
      summarizeTimeChart(item as ITimeChart, model, result).forEach((line) => lines.push(`${indent}  ${line}`));
      break;
    }
    case "3d_surface": {
      pushLine(`${formatDisplayLabel(item)} (3d surface):`);
      summarize3dSurface(item as I3dSurface, result).forEach((line) => lines.push(`${indent}  ${line}`));
      break;
    }
    case "raw_plotly_chart": {
      pushLine(`${formatDisplayLabel(item)} (plotly chart):`);
      summarizeRawPlotly(item as IRawPlotlyChart, model, result).forEach((line) => lines.push(`${indent}  ${line}`));
      break;
    }
    case "markdown": {
      const content = (item as IOutput).label || "";
      if (content) {
        pushLine(`${formatDisplayLabel(item)}: ${content}`);
      }
      break;
    }
    case "image":
    case "select":
    case "radio":
    case "checkbox":
    case "calculate":
    default: {
      const value = getEffectiveValue(model, result, item.name);
      const isValueEmpty =
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && !Array.isArray(value) && isEmpty(value));
      if (isValueEmpty) {
        return;
      }
      pushLine(`${formatDisplayLabel(item)}: ${stringifyValue(value)}`);
    }
  }
};

const collectOutputControls = (
  item: IBase,
  model: IModel,
  result: IResult,
  controls: IAssistantOutputControlSummary[],
  metadata?: IMetadata,
) => {
  if (isOutputItemHidden(item, model, result, metadata)) {
    return;
  }

  switch (item.type) {
    case "group":
    case "anchors":
    case "tabs":
    case "tab":
    case "explorer":
      (item as any).items?.forEach((child: IBase) => collectOutputControls(child, model, result, controls, metadata));
      return;
    default:
      if (!OUTPUT_CONTROL_TYPES.has(item.type)) {
        return;
      }

      const value = getEffectiveValue(model, result, item.name);
      controls.push({
        path: item.name,
        label: formatDisplayLabel(item),
        type: item.type,
        value,
        checked: item.type === "checkbox" ? Boolean(value) : undefined,
        disabled: isOutputControlDisabled(item, model, result),
      });
  }
};

const collectChartContexts = (
  item: IBase,
  model: IModel,
  result: IResult,
  charts: IAssistantChartContext[],
  metadata?: IMetadata,
) => {
  if (isOutputItemHidden(item, model, result, metadata)) {
    return;
  }

  switch (item.type) {
    case "group":
    case "anchors":
    case "tabs":
    case "tab":
    case "explorer":
      (item as any).items?.forEach((child: IBase) => collectChartContexts(child, model, result, charts, metadata));
      return;
    default:
      if (!CHART_OUTPUT_TYPES.has(item.type)) {
        return;
      }

      const chartContext = buildChartContext(item, model, result, metadata);
      if (chartContext) {
        charts.push(chartContext);
      }
  }
};

export const buildOutputSummaryText = (
  template: ITemplate,
  model: IModel,
  result: IResult,
  metadata?: IMetadata,
): string => {
  if (!template?.outputs?.length) {
    return "";
  }
  const lines: string[] = [];
  template.outputs.forEach((item) => renderOutputItem(item, model, result, lines, 0, metadata));
  return lines.join("\n");
};

export const buildAssistantOutputControlSummary = (
  template: ITemplate,
  model: IModel,
  result: IResult,
  metadata?: IMetadata,
): IAssistantOutputControlSummary[] => {
  if (!template?.outputs?.length) {
    return [];
  }

  const controls: IAssistantOutputControlSummary[] = [];
  template.outputs.forEach((item) => collectOutputControls(item, model, result, controls, metadata));
  return controls;
};

export const buildAssistantChartContexts = (
  template: ITemplate,
  model: IModel,
  result: IResult,
  metadata?: IMetadata,
): IAssistantChartContext[] => {
  if (!template?.outputs?.length) {
    return [];
  }

  const charts: IAssistantChartContext[] = [];
  template.outputs.forEach((item) => collectChartContexts(item, model, result, charts, metadata));
  return charts;
};
